import {
    getMySharedAssets,
    increaseMySharedAssetsDownloadCount,
    resetMySharedAssetsList,
} from "@modules/shared-box-v2";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAbortController from "@hooks/useAbortController";
import VersionedComponent from "@components/VersionedComponent";
import { VERSION_CM_13766_SHARES_V3 } from "@cores/version";
import { Button, CircularProgress, Grid, Typography } from "@mzc-pdc/ui";
import EmptyList from "@components_v2/table-list/empty-list";
import TableList from "@components_v2/table-list/table-list";
import { togglePropertyPanel } from "@modules/propertyPanel";
import { IconDownload } from "@mzc-cloudplex/icons";
import { TABLE_LIST_TYPE } from "@constants";
import ListTools from "@features/list-tools/list-tools";
import useDialog from "@hooks/useDialog";
import { Pagination } from "@components_v2/pgaination/pagination";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { MySharedAssetsColumn } from "./columns-list-shared-v2";
import { withSearchQueryParamsContext } from "@components/params";
import * as yup from "yup";
import useDownloadJobs from "@hooks/useDownloadJobs";
import { listSharedAssetProjectsAPI } from "@modules/apis/shared-box-v2";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import moment from "moment/moment";
import { siteMode } from "@cores/siteMode";

const ListSharedAssetsWithMe = ({ searchQueryParams, updateFilterValueOptions }) => {
    const { spaceId, projectId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getNewController, abortAll, handleApiResult } = useAbortController();
    const { dialog, openDialog, closeDialog } = useDialog();
    const { requestDownloadJob } = useDownloadJobs();
    const isLibrary = siteMode.get() === "CMS";

    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });
    const [clickedItem, setClickedItem] = useState(null);

    const stage = useSelector(({ stage }) => stage);
    const sharedBoxV2 = useSelector(({ sharedBoxV2 }) => sharedBoxV2);

    const onHandleChangeOffset = (page) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: page, data: prevState.data }));
    };

    const onHandleChangeLimit = (limit) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { ...searchQueryParams.params, limit },
        });
    };

    const loadSharedAssetsWithMe = async () => {
        try {
            const signal = getNewController("mySharedAssets");
            const { currentPage, data: nextTokenList } = tokenPaginationInfo;

            const params = {
                nextToken: currentPage > 0 ? nextTokenList[currentPage] : undefined,
                limit: searchQueryParams?.params?.limit || 20,
                projectIds: searchQueryParams?.params?.projectIds?.map((project) => project.id)?.join(","),
            };

            const response = await dispatch(getMySharedAssets(params));
            handleApiResult(response);
            nextTokenList[currentPage + 1] = response?.nextToken;
            setTokenPaginationInfo({ currentPage, data: nextTokenList });
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickDownload = useCallback(
        async (target) => {
            try {
                const downloadRequestTargets = {
                    resourceId: target?.assetId,
                    projectId: target?.project?.id,
                    resourceName: target?.name,
                };
                await requestDownloadJob("ASSET", downloadRequestTargets, 1, false);
                dispatch(increaseMySharedAssetsDownloadCount(target));
            } catch (error) {
                console.log(error);
            }
        },
        [projectId, requestDownloadJob],
    );

    const onHandleClickRefresh = () => {
        loadSharedAssetsWithMe();
    };

    const pagination = useMemo(() => {
        return (
            <Pagination
                page={tokenPaginationInfo.currentPage}
                limit={searchQueryParams.params.limit}
                hasNextToken={tokenPaginationInfo.data[tokenPaginationInfo.currentPage + 1] !== undefined}
                onChangePage={onHandleChangeOffset}
                onChangeLimit={onHandleChangeLimit}
                onClickRefresh={onHandleClickRefresh}
            />
        );
    }, [tokenPaginationInfo, searchQueryParams?.params?.limit]);

    const parsedData = useMemo(() => {
        return sharedBoxV2?.data?.map(({ asset, id, ...rest }) => ({
            ...(asset ?? {}),
            id,
            assetId: asset?.id,
            expiresAt: rest?.option?.expiresAt,
            created: { at: asset?.createdAt, by: asset?.createdBy },
            ...rest,
        }));
    }, [sharedBoxV2?.data]);

    const getFilterOptions = useCallback(async (filterIds) => {
        const results = {};

        // 각 필터 ID에 대해 병렬로 처리
        await Promise.all(
            filterIds.map(async (filterId) => {
                switch (filterId) {
                    case "projectIds":
                        const projects = [];
                        let nextToken = null;
                        do {
                            const response = await listSharedAssetProjectsAPI(stage.endpoint, {
                                nextToken,
                                limit: 20,
                            });

                            projects.push(...response?.data?.results);
                            nextToken = response?.data?.nextToken;
                        } while (nextToken);

                        results[filterId] = projects.map(({ id, name }) => ({
                            id: id,
                            label: name,
                            value: id,
                            name: name,
                        }));
                        break;
                }
            }),
        );

        updateFilterValueOptions(results);
    }, []);

    const getDownloadDisabled = useCallback((item) => {
        if (!item) return true;
        const downloadsRemaining =
            !item.option?.maxDownloads || item.option?.maxDownloads - (item.option?.downloadCount ?? 0) > 0;
        const isExpired = countDaysBetweenDates({ toDate: moment(item.option?.expiresAt) }) < 0;
        return !downloadsRemaining || isExpired;
    }, []);

    useEffect(() => {
        searchQueryParams.resetSearchQueryParams({
            action: "replace",
            params: {
                ...searchQueryParams.params,
                limit: searchQueryParams?.params?.limit ?? 20,
                type: "SHARED_WITH_ME",
            },
        });

        getFilterOptions(["projectIds"]);

        return () => {
            abortAll();
            dispatch(resetMySharedAssetsList());
        };
    }, []);

    useEffect(() => {
        if (!searchQueryParams?.params?.limit) {
            return;
        }
        loadSharedAssetsWithMe();
    }, [searchQueryParams?.params, tokenPaginationInfo?.currentPage]);

    return (
        <VersionedComponent version={VERSION_CM_13766_SHARES_V3}>
            <>
                <ListTools
                    domain={TABLE_LIST_TYPE.MY_SHARED_ASSETS}
                    isCardView={false}
                    pageCount={sharedBoxV2?.data?.length}
                    hasNextToken={tokenPaginationInfo.data[tokenPaginationInfo.currentPage] !== undefined}
                    onChangePage={onHandleChangeOffset}
                    onChangeLimit={onHandleChangeLimit}
                    pagination={pagination}
                    hiddenDivider={true}
                    hideCheckbox={true}
                />
                {sharedBoxV2?.pending ? (
                    <Grid item xs>
                        <Grid container alignItems={"center"} justifyContent={"center"} height={`100%`}>
                            <CircularProgress color="grey" />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {parsedData?.length > 0 ? (
                            <TableList
                                keyPrefix={"my-shared-assets"}
                                type={"share_asset"}
                                columns={MySharedAssetsColumn}
                                data={parsedData}
                                clicked={clickedItem}
                                showColumns={true}
                                onClickRow={(item) => {
                                    setClickedItem(item);
                                    if (isLibrary || !item.assetId) return;
                                    dispatch(
                                        togglePropertyPanel({
                                            targetId: item.assetId,
                                            projectId: item.project.id,
                                            type: TABLE_LIST_TYPE.ASSET,
                                        }),
                                    );
                                }}
                                actionButton={(item) => (
                                    <Button
                                        size={"small"}
                                        color={"greySecondary"}
                                        variant="outlined"
                                        disabled={getDownloadDisabled(item) || !item.assetId}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onHandleClickDownload(item);
                                        }}
                                    >
                                        <Typography>{t("common::label::Download", "Download")}</Typography>
                                    </Button>
                                )}
                                actionButtonProps={{ width: 128 }}
                            />
                        ) : (
                            <EmptyList />
                        )}
                    </>
                )}
            </>
        </VersionedComponent>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        projectIds: yup.array(),
        activeView: yup.string(),
    },
    setProps: true,
})(ListSharedAssetsWithMe);
